import { useEffect, useState } from 'react';
import { Flag, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { cn } from '@/lib/utils';

const copy = {
  en: { save: 'Save', report: 'Report', saved: 'Saved', reportTitle: 'Report This Message', reportDesc: 'If this message is inappropriate or makes you feel unsafe, please let us know.', reportReason: 'Reason', reportReasons: ['Harassment or abuse', 'Inappropriate content', 'Spam', 'Makes me feel unsafe', 'Other'], reportDetails: 'Additional details (optional)', submitReport: 'Submit Report', reportSuccess: 'Report submitted. Our support team has been notified.', reportFail: 'Failed to submit report. Please try again.', saveSuccessAuth: 'Saved to your collection.', saveSuccessGuest: 'Create a free account to save this message and access it anytime.', alreadySaved: 'Already saved!', signUp: 'Sign Up' },
  es: { save: 'Guardar', report: 'Reportar', saved: 'Guardado', reportTitle: 'Reportar Este Mensaje', reportDesc: 'Si este mensaje es inapropiado o te hace sentir inseguro/a, por favor dínoslo.', reportReason: 'Razón', reportReasons: ['Acoso o abuso', 'Contenido inapropiado', 'Spam', 'Me hace sentir inseguro/a', 'Otro'], reportDetails: 'Detalles adicionales (opcional)', submitReport: 'Enviar Reporte', reportSuccess: 'Reporte enviado. Nuestro equipo de soporte ha sido notificado.', reportFail: 'Error al enviar el reporte. Inténtalo de nuevo.', saveSuccessAuth: 'Guardado en tu colección.', saveSuccessGuest: 'Crea una cuenta gratuita para guardar este mensaje y acceder a él en cualquier momento.', alreadySaved: '¡Ya guardado!', signUp: 'Registrarse' },
  fr: { save: 'Sauvegarder', report: 'Signaler', saved: 'Sauvegardé', reportTitle: 'Signaler ce Message', reportDesc: 'Si ce message est inapproprié ou vous met mal à l\'aise, veuillez nous en informer.', reportReason: 'Raison', reportReasons: ['Harcèlement ou abus', 'Contenu inapproprié', 'Spam', 'Me met mal à l\'aise', 'Autre'], reportDetails: 'Détails supplémentaires (optionnel)', submitReport: 'Soumettre', reportSuccess: 'Signalement soumis. Notre équipe a été notifiée.', reportFail: 'Échec de l\'envoi. Veuillez réessayer.', saveSuccessAuth: 'Sauvegardé dans votre collection.', saveSuccessGuest: 'Créez un compte gratuit pour sauvegarder ce message et y accéder à tout moment.', alreadySaved: 'Déjà sauvegardé !', signUp: 'S\'inscrire' },
  pt: { save: 'Salvar', report: 'Denunciar', saved: 'Salvo', reportTitle: 'Denunciar Esta Mensagem', reportDesc: 'Se esta mensagem é inapropriada ou faz você se sentir inseguro/a, por favor nos avise.', reportReason: 'Razão', reportReasons: ['Assédio ou abuso', 'Conteúdo inapropriado', 'Spam', 'Me faz sentir inseguro/a', 'Outro'], reportDetails: 'Detalhes adicionais (opcional)', submitReport: 'Enviar Denúncia', reportSuccess: 'Denúncia enviada. Nossa equipe foi notificada.', reportFail: 'Falha ao enviar. Tente novamente.', saveSuccessAuth: 'Salvo na sua coleção.', saveSuccessGuest: 'Crie uma conta gratuita para salvar esta mensagem e acessá-la a qualquer momento.', alreadySaved: 'Já salvo!', signUp: 'Cadastrar-se' },
  ar: { save: 'حفظ', report: 'إبلاغ', saved: 'محفوظ', reportTitle: 'الإبلاغ عن هذه الرسالة', reportDesc: 'إذا كانت هذه الرسالة غير لائقة أو تشعرك بعدم الأمان، يرجى إبلاغنا.', reportReason: 'السبب', reportReasons: ['مضايقة أو إساءة', 'محتوى غير لائق', 'رسائل مزعجة', 'يجعلني أشعر بعدم الأمان', 'آخر'], reportDetails: 'تفاصيل إضافية (اختياري)', submitReport: 'إرسال البلاغ', reportSuccess: 'تم إرسال البلاغ. تم إخطار فريق الدعم.', reportFail: 'فشل إرسال البلاغ. حاول مجدداً.', saveSuccessAuth: 'تم الحفظ في مجموعتك.', saveSuccessGuest: 'أنشئ حساباً مجانياً لحفظ هذه الرسالة والوصول إليها في أي وقت.', alreadySaved: 'تم الحفظ بالفعل!', signUp: 'إنشاء حساب' },
  ja: { save: '保存', report: '報告', saved: '保存済み', reportTitle: 'このメッセージを報告', reportDesc: 'このメッセージが不適切、または不安を感じる場合はお知らせください。', reportReason: '理由', reportReasons: ['ハラスメントまたは虐待', '不適切なコンテンツ', 'スパム', '不安を感じる', 'その他'], reportDetails: '追加の詳細（任意）', submitReport: '報告を送信', reportSuccess: '報告が送信されました。サポートチームに通知しました。', reportFail: '報告の送信に失敗しました。もう一度お試しください。', saveSuccessAuth: 'コレクションに保存しました。', saveSuccessGuest: '無料アカウントを作成して、このメッセージを保存していつでもアクセスできます。', alreadySaved: 'すでに保存済みです！', signUp: '新規登録' },
  ko: { save: '저장', report: '신고', saved: '저장됨', reportTitle: '이 메시지 신고', reportDesc: '이 메시지가 부적절하거나 불안하게 느껴지면 알려주세요.', reportReason: '이유', reportReasons: ['괴롭힘 또는 학대', '부적절한 콘텐츠', '스팸', '불안감을 줌', '기타'], reportDetails: '추가 정보 (선택)', submitReport: '신고 제출', reportSuccess: '신고가 제출되었습니다. 지원팀에 통보되었습니다.', reportFail: '신고 제출에 실패했습니다. 다시 시도해주세요.', saveSuccessAuth: '컬렉션에 저장되었습니다.', saveSuccessGuest: '무료 계정을 만들어 이 메시지를 저장하고 언제든 접근하세요.', alreadySaved: '이미 저장되었습니다!', signUp: '가입하기' },
  id: { save: 'Simpan', report: 'Laporkan', saved: 'Tersimpan', reportTitle: 'Laporkan Pesan Ini', reportDesc: 'Jika pesan ini tidak pantas atau membuat Anda tidak nyaman, silakan beri tahu kami.', reportReason: 'Alasan', reportReasons: ['Pelecehan atau penyalahgunaan', 'Konten tidak pantas', 'Spam', 'Membuat saya tidak nyaman', 'Lainnya'], reportDetails: 'Detail tambahan (opsional)', submitReport: 'Kirim Laporan', reportSuccess: 'Laporan terkirim. Tim dukungan kami telah diberitahu.', reportFail: 'Gagal mengirim laporan. Silakan coba lagi.', saveSuccessAuth: 'Tersimpan di koleksi Anda.', saveSuccessGuest: 'Buat akun gratis untuk menyimpan pesan ini dan mengaksesnya kapan saja.', alreadySaved: 'Sudah tersimpan!', signUp: 'Daftar' },
  hi: { save: 'सहेजें', report: 'रिपोर्ट करें', saved: 'सहेजा गया', reportTitle: 'इस संदेश की रिपोर्ट करें', reportDesc: 'यदि यह संदेश अनुचित है या आपको असुरक्षित महसूस कराता है, तो कृपया हमें बताएं।', reportReason: 'कारण', reportReasons: ['उत्पीड़न या दुर्व्यवहार', 'अनुचित सामग्री', 'स्पैम', 'मुझे असुरक्षित महसूस कराता है', 'अन्य'], reportDetails: 'अतिरिक्त विवरण (वैकल्पिक)', submitReport: 'रिपोर्ट सबमिट करें', reportSuccess: 'रिपोर्ट सबमिट हो गई। हमारी सहायता टीम को सूचित कर दिया गया है।', reportFail: 'रिपोर्ट सबमिट करने में विफल। कृपया पुनः प्रयास करें।', saveSuccessAuth: 'आपके संग्रह में सहेजा गया।', saveSuccessGuest: 'इस संदेश को सहेजने के लिए मुफ़्त खाता बनाएं और कभी भी एक्सेस करें।', alreadySaved: 'पहले ही सहेज लिया गया है!', signUp: 'साइन अप करें' },
};

interface RecipientActionsProps {
  announcementId: string;
  publicSlug: string;
}

export function RecipientActions({ announcementId, publicSlug }: RecipientActionsProps) {
  const { language } = useLanguage();
  const c = lc(copy, language);
  const [reportOpen, setReportOpen] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');

  useEffect(() => {
    let isMounted = true;

    const checkSavedState = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        // Only check saved state for authenticated users
        if (!user) {
          if (isMounted) setIsSaved(false);
          return;
        }

        const { data, error } = await (supabase as any)
          .from('saved_announcements')
          .select('id')
          .eq('announcement_slug', publicSlug)
          .eq('saver_identifier', user.id)
          .maybeSingle();

        if (!isMounted) return;

        if (error) {
          console.error('Saved status lookup error:', error);
          return;
        }

        setIsSaved(!!data);
      } catch (error) {
        console.error('Saved status lookup failed:', error);
      }
    };

    checkSavedState();

    return () => {
      isMounted = false;
    };
  }, [publicSlug]);

  const handleSave = async () => {
    if (isSaved) {
      toast.info(c.alreadySaved);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Guest users: don't save, show signup CTA instead
      if (!user) {
        toast(c.saveSuccessGuest, {
          duration: 10000,
          closeButton: true,
          position: 'top-center',
          className: 'w-[calc(100vw-2rem)] max-w-sm sm:max-w-md',
          classNames: {
            title: 'whitespace-normal text-sm leading-5 pr-2',
            actionButton: 'min-h-10 px-4 text-sm font-medium',
          },
          action: {
            label: c.signUp,
            onClick: () => { window.location.href = '/signup'; },
          },
        });
        return;
      }

      // Authenticated users: save with user ID
      const { error } = await (supabase as any).from('saved_announcements').insert({
        announcement_slug: publicSlug,
        saver_identifier: user.id,
      });

      if (error) {
        if (error.code === '23505') {
          setIsSaved(true);
          toast.info(c.alreadySaved);
          return;
        }

        console.error('Save announcement error:', error);
        toast.error('Unable to save right now. Please try again.');
        return;
      }

      setIsSaved(true);
      toast.success(c.saveSuccessAuth);
    } catch (error) {
      console.error('Save announcement unexpected error:', error);
      toast.error('Unable to save right now. Please try again.');
    }
  };

  const handleReport = async () => {
    if (!reportReason) return;
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error: insertError } = await (supabase as any).from('announcement_reports').insert({
        announcement_id: announcementId,
        reporter_reason: reportReason,
        reporter_details: reportDetails.trim() || null,
      });

      if (insertError) {
        console.error('Report insert error:', insertError);
        toast.error(c.reportFail);
        return;
      }

      const { data: notifyResult, error: fnError } = await supabase.functions.invoke('notify-report', {
        body: {
          announcement_id: announcementId,
          reporter_reason: reportReason,
          reporter_details: reportDetails.trim() || null,
          announcement_url: typeof window !== 'undefined' ? window.location.href : null,
          submitted_at: new Date().toISOString(),
          source: 'user_report',
          user_id: user?.id ?? null,
          user_email: user?.email ?? null,
        },
      });

      if (fnError || (notifyResult && typeof notifyResult === 'object' && 'success' in notifyResult && !notifyResult.success)) {
        console.error('notify-report error:', fnError || notifyResult);
        toast.error(c.reportFail);
        return;
      }

      toast.success(c.reportSuccess);
      setReportOpen(false);
      setReportReason('');
      setReportDetails('');
    } catch (err) {
      console.error('Report submission error:', err);
      toast.error(c.reportFail);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Save + Report only — reply is handled by AnonymousReplyThread */}
      <div className="flex items-center justify-center gap-3 py-6">
        <Button
          variant="outline"
          size="sm"
          onClick={handleSave}
          className={cn('gap-2', isSaved && 'text-primary border-primary/20')}
        >
          <Heart className={cn('h-4 w-4', isSaved && 'fill-current')} />
          {isSaved ? c.saved : c.save}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setReportOpen(true)} className="gap-2 text-muted-foreground">
          <Flag className="h-4 w-4" />
          {c.report}
        </Button>
      </div>

      {/* Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{c.reportTitle}</DialogTitle>
            <DialogDescription>{c.reportDesc}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm mb-2 block">{c.reportReason}</Label>
              <div className="space-y-2">
                {c.reportReasons.map((reason: string, i: number) => (
                  <button
                    key={i}
                    onClick={() => setReportReason(reason)}
                    className={cn(
                      'w-full p-3 rounded-lg border text-left text-sm transition-all',
                      reportReason === reason
                        ? 'border-destructive bg-destructive/5 text-foreground'
                        : 'border-border hover:border-destructive/50 text-muted-foreground'
                    )}
                  >
                    {reason}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="reportDetails" className="text-sm">{c.reportDetails}</Label>
              <Textarea
                id="reportDetails"
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                className="mt-1 min-h-[80px]"
                maxLength={500}
              />
            </div>
            <Button
              variant="destructive"
              onClick={handleReport}
              disabled={!reportReason || isSubmitting}
              className="w-full gap-2"
            >
              <Flag className="h-4 w-4" />
              {c.submitReport}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
